import Modal from "@/components/modal";
import { FC, useEffect, useRef, useState } from "react";
import styles from "./index.module.scss";
import { Button } from "@/components/button";
import { Trans, useTranslation } from "react-i18next";
import isE2e from "@/helpers/isE2e";
import { playSound, SoundOption } from "@/helpers/playSound";
import useNavigateToStart from "@/hooks/useNavigateToStart";

const defaultSecondsBeforeModal = isE2e ? 5 : 30;
const secondsBeforeRedirect = isE2e ? 5 : 30;

interface StillThereModalProps {
  secondsBeforeModal?: number;
}

const StillThereModal: FC<StillThereModalProps> = ({
  secondsBeforeModal = defaultSecondsBeforeModal,
}) => {
  const navigateToStart = useNavigateToStart();
  const { t } = useTranslation();
  const [secondsBeforeNextCustomer, setSecondsBeforeNextCustomer] = useState<number>();
  const [latestTimeout, setLatestTimeout] = useState<NodeJS.Timeout>();
  const latestTimeoutRef = useRef<NodeJS.Timeout | undefined>(latestTimeout);
  latestTimeoutRef.current = latestTimeout;
  const countDownTimeout = useRef<NodeJS.Timeout>();
  const visible = secondsBeforeNextCustomer !== undefined;

  const stillHere = () => {
    if (!!countDownTimeout.current) clearTimeout(countDownTimeout.current);
    setSecondsBeforeNextCustomer(undefined);
  };

  const onNextCustomer = () => {
    playSound(SoundOption.NextCustomer);
    navigateToStart();
  };

  useEffect(() => {
    const onActivity = () => {
      stillHere();
      if (latestTimeoutRef.current !== undefined) clearTimeout(latestTimeoutRef.current);
      const timeout = setTimeout(() => {
        setSecondsBeforeNextCustomer(secondsBeforeRedirect);
      }, secondsBeforeModal * 1000);
      setLatestTimeout(timeout);
    };
    onActivity();
    document.addEventListener("click", onActivity);
    document.addEventListener("keydown", onActivity);
    return () => {
      document.removeEventListener("click", onActivity);
      document.removeEventListener("keydown", onActivity);
      clearTimeout(latestTimeoutRef.current);
      clearTimeout(countDownTimeout.current);
    };
  }, []);

  useEffect(() => {
    if (secondsBeforeNextCustomer === undefined) return;
    if (secondsBeforeNextCustomer === 0) {
      playSound(SoundOption.NextCustomer);
      navigateToStart();
    }
    countDownTimeout.current = setTimeout(() => {
      setSecondsBeforeNextCustomer(secondsBeforeNextCustomer - 1);
    }, 1000);
  }, [secondsBeforeNextCustomer]);

  return (
    <Modal title={t("components.stillHereModal.title")} visible={visible} onClose={stillHere}>
      <p className={styles.text} data-cy="still-there-text">
        <Trans i18nKey="components.stillHereModal.closingIn" count={secondsBeforeNextCustomer}>
          <b />
        </Trans>
      </p>

      <div className={styles.row}>
        <a className={styles.nextCustomer} onClick={onNextCustomer} data-cy="next-customer-button">
          {t("components.stillHereModal.nextCustomer")}
        </a>
        <Button
          text={t("components.stillHereModal.stillHere")}
          onClick={stillHere}
          size="x-small"
          className={styles.stillHereButton}
          data-cy="still-here-button"
        />
      </div>
    </Modal>
  );
};

export { StillThereModal };
